<script setup lang="ts">
import 'swiper/css'

import useProductStore from '@/stores/product'
import { storeToRefs } from 'pinia'
import type { Swiper } from 'swiper/types'
import { Swiper as AppSwiper, SwiperSlide } from 'swiper/vue'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AddToWishlist from '../common/AddToWishlist.vue'
import AppImage from '../common/AppImage.vue'
import OfficialMerchantBadge from '../common/OfficialMerchantBadge.vue'
import ImageSlideIndicator from './ImageSlideIndicator.vue'

const props = defineProps<{
  product: Product
}>()

const router = useRouter()

const productStore = useProductStore()

const { activeImageIndex } = storeToRefs(productStore)
const swiper = ref<Swiper>()

const images = computed(() => {
  return props.product.images
})

watch(
  () => swiper.value?.activeIndex,
  (newActiveIndex) => {
    if (typeof newActiveIndex === 'number') {
      activeImageIndex.value = newActiveIndex
    }
  }
)

const onSwiper = (instance: Swiper) => {
  swiper.value = instance
  swiper.value.slideTo(activeImageIndex.value)
}

const showProductGallery = () => {
  router.push({
    name: 'product.gallery',
    params: {
      id: props.product.id,
      slug: props.product.slug
    }
  })
}
</script>

<template>
  <div v-if="images?.length">
    <AppSwiper
      :resistance-ratio="0"
      class="relative"
      @swiper="onSwiper"
    >
      <SwiperSlide
        v-for="(image, index) in images"
        :key="index"
        @click="showProductGallery"
      >
        <AppImage
          v-if="image.image_url"
          :src="image.image_url"
          :alt="product.name"
          :width="480"
          :height="480"
          class="aspect-square w-full bg-white object-cover md:rounded-16"
        />
      </SwiperSlide>

      <!-- Wishlist Button -->
      <div class="absolute right-16 top-16 z-10 md:hidden">
        <AddToWishlist
          :product="product"
          round
        />
      </div>
      <!-- Images Stats -->
      <div class="absolute bottom-16 left-16 z-10 lg:hidden">
        <ImageSlideIndicator
          v-if="images && images.length > 1 && activeImageIndex.toString()"
          :current="activeImageIndex + 1"
          :total="images.length"
        />
      </div>
      <!-- Official Merchant Badge -->
      <div
        v-if="product.shop"
        class="absolute bottom-0 right-0 z-10"
      >
        <OfficialMerchantBadge
          class="w-fit"
          rounded-class="rounded-tl-24 md:rounded-br-[13px] lg:rounded-br-[14px]"
          large
        />
      </div>
    </AppSwiper>
  </div>
</template>
